import React from 'react';
import { CircularProgress } from '@mui/material';
import FieldSwitch from 'components/FormElements/FieldSwitch';
import { useFundFullyDeployed } from 'hooks/data/fund/useFundFullyDeployed';
import { FundAllocation } from '../index';
import { UseFormSetValue } from 'react-hook-form';

type FullyDeployToggleProps = {
  fundAllocation?: {
    trancheId?: string;
    isFullyDeployed?: boolean;
  };
  fundIndex: number;
  setValue: UseFormSetValue<{ fundAllocations: Array<FundAllocation> }>;
  isEditing: boolean;
  isDraftAllocation: boolean;
  fundIdsInDraftAllocation: Array<string>;
};

const FundEditHeaderbar: React.FC<FullyDeployToggleProps> = ({
  fundAllocation,
  fundIndex,
  setValue,
  isEditing,
  isDraftAllocation,
  fundIdsInDraftAllocation,
}) => {
  const { undoFundDeployment } = useFundFullyDeployed({
    fundId: fundAllocation?.trancheId ?? '',
    viaAllocationPreview: true,
  });

  const handleFullyDeployedToggle = async () => {
    try {
      await undoFundDeployment.mutateAsync();

      setValue(
        `fundAllocations.${fundIndex}.isFullyDeployed`,
        !fundAllocation?.isFullyDeployed,
      );
    } catch (error) {
      console.error(error);
    }
  };

  const fundHasDraftAllocation =
    isDraftAllocation ||
    fundIdsInDraftAllocation.includes(fundAllocation?.trancheId ?? '');

  if (
    fundHasDraftAllocation ||
    fundIdsInDraftAllocation.includes(fundAllocation?.trancheId ?? '')
  ) {
    return (
      <>
        This tranche is part of an existing draft allocation. Editing this
        tranche's allocation will delete the existing draft for all tranches in
        that draft allocation.
      </>
    );
  }

  if (!isEditing || !fundAllocation?.isFullyDeployed) {
    return null;
  }

  return (
    <>
      {undoFundDeployment.isPending ? (
        <CircularProgress />
      ) : (
        <>
          This tranche is marked "fully deployed" and is uneditable. To enable
          edit, toggle the setting here or on the Edit Tranche page.
          <FieldSwitch
            checkedLabel="Fully deployed"
            uncheckedLabel="Not fully deployed"
            checked={fundAllocation.isFullyDeployed}
            onChange={handleFullyDeployedToggle}
          />
        </>
      )}
    </>
  );
};

export default FundEditHeaderbar;
